// Where this lib persists the session token, and how a host swaps the backing
// store. Self-contained on purpose: the lib ships an in-memory default so it
// runs unconfigured, and a host injects a real driver once at boot — the same
// object shape the app SDK's device-storage port already produces
// (`get`/`set`/`remove`, each async and each guaranteed to resolve, never
// reject, because these run on the boot path before anything mounts).
//
// This is deliberately NOT the app SDK's shared ports system. A reusable
// identity client cannot depend up into a host's port registry, so it owns its
// own tiny storage seam. A host that already has a device-storage instance
// passes the same one in — one driver, two consumers, no shared package.

function createMemoryStorage() {
    const map = new Map();
    return {
        async get(key) {
            return map.has(key) ? map.get(key) : null;
        },
        async set(key, value) {
            map.set(key, value);
        },
        async remove(key) {
            map.delete(key);
        }
    };
}

let storage = createMemoryStorage();

/**
 * Install the host's storage. The value must answer `get(key)`, `set(key,
 * value)` and `remove(key)`, each a promise that resolves (never rejects).
 */
export function configureIdentityStorage(driver) {
    if (driver) {
        storage = driver;
    }
    return storage;
}

export function useIdentityStorage() {
    return storage;
}

/** Put storage back to the in-memory default. For tests. */
export function resetIdentityStorage() {
    storage = createMemoryStorage();
    return storage;
}

export { createMemoryStorage };

// ── The proof key's home ──────────────────────────────────────────────────
//
// The session token is bound to a key this browser made and cannot export. A
// CryptoKey is not a string, so it cannot ride the storage seam above: the one
// place a web page can keep one is IndexedDB, which stores the key object itself
// without ever revealing its private half. That is the default wherever
// IndexedDB exists. Anywhere it does not (a test, a server render) the key lives
// in memory and dies with the page.
//
// A driver answers `get(id)`, `set(id, keyPair)` and `remove(id)`, each a
// promise. A host with a platform keystore installs its own.

const KEY_DATABASE = 'aac.identity';
const KEY_OBJECT_STORE = 'proof-keys';

function createMemoryKeyStore() {
    const map = new Map();
    return {
        async get(id) {
            return map.has(id) ? map.get(id) : null;
        },
        async set(id, keyPair) {
            map.set(id, keyPair);
        },
        async remove(id) {
            map.delete(id);
        }
    };
}

function createIndexedDbKeyStore(indexedDb) {
    function settled(request) {
        return new Promise((resolve, reject) => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }
    async function objectStore(mode) {
        const opening = indexedDb.open(KEY_DATABASE, 1);
        opening.onupgradeneeded = () => {
            opening.result.createObjectStore(KEY_OBJECT_STORE);
        };
        const database = await settled(opening);
        return database.transaction(KEY_OBJECT_STORE, mode).objectStore(KEY_OBJECT_STORE);
    }
    return {
        async get(id) {
            return (await settled((await objectStore('readonly')).get(id))) ?? null;
        },
        async set(id, keyPair) {
            await settled((await objectStore('readwrite')).put(keyPair, id));
        },
        async remove(id) {
            await settled((await objectStore('readwrite')).delete(id));
        }
    };
}

function defaultKeyStore() {
    if (typeof globalThis.indexedDB === 'object' && globalThis.indexedDB !== null) {
        return createIndexedDbKeyStore(globalThis.indexedDB);
    }
    return createMemoryKeyStore();
}

let keyStore = null;

/** Install the host's key store: `get(id)`, `set(id, keyPair)`, `remove(id)`, each a promise. */
export function configureIdentityKeyStore(driver) {
    if (driver) {
        keyStore = driver;
    }
    return keyStore;
}

export function useIdentityKeyStore() {
    if (keyStore === null) {
        keyStore = defaultKeyStore();
    }
    return keyStore;
}

/** Put the key store back to its default. For tests. */
export function resetIdentityKeyStore() {
    keyStore = null;
}

export { createMemoryKeyStore, createIndexedDbKeyStore };

// ── The native sign-in sheet ──────────────────────────────────────────────
//
// Inside the iPhone app, a sign-in that sends the WebView itself to the hosted
// login comes back to a page whose web storage is gone: the proof key with it.
// The code the identity server returns is bound to that lost key, so the
// exchange is refused (INVALID_GRANT). The native shell answers with the system
// sign-in sheet (ASWebAuthenticationSession): the hosted login runs in the sheet,
// the app's page never leaves, and its key stays. RFC 8252 is the same rule.
//
// The sheet and the address it returns to belong to the app, not to this lib,
// so the host installs both: `redirectUri` is the app's own return address (a
// custom scheme the sheet catches on the device, never loaded over a network),
// and `open(url)` resolves the URL the sign-in came back to, or null when the
// person closed the sheet. Only a native shell installs one; a browser has none.

let signInSheet = null;

/** Install the host's sign-in sheet: `{ redirectUri, open(url) }`. */
export function configureSignInSheet(driver) {
    if (driver) {
        signInSheet = driver;
    }
    return signInSheet;
}

/** The installed sign-in sheet, or null when this host has none. */
export function useSignInSheet() {
    return signInSheet;
}

/** Remove the sign-in sheet. For tests. */
export function resetSignInSheet() {
    signInSheet = null;
}
